<template>
    <div aria-live="assertive" class="online-tests-new-page">
        <div class="col-xs-12">
            <div class="d-flex-row flex-justify-between">
                <p id="title" role="heading" aria-level="1" tabindex="0">
                    <i class="fa fa-globe"></i> Provas online
                </p>
            </div>
        </div>

        <div v-if="!loading">
            <div v-if="result && result.length > 0">
                <div class="col-xs-12 p-x-sm d-flex-row flex-wrap">
                    <div class="col-xs-12 p-md m-b-md">
                        <div class="card p-20 filter-card">
                            <div class="search-input-wrapper m-b-xl">
                                <i class="fa fa-search"></i>
                                <input
                                    type="text"
                                    v-model="searchQuery"
                                    placeholder="Buscar por disciplina ou avaliação..."
                                    class="search-input"
                                />
                            </div>

                            <div class="finished-filter-row">
                                <FilterMultiDropdown
                                    label="Módulo"
                                    icon="fa fa-filter"
                                    :options="moduleOptions"
                                    v-model="selectedModules"
                                />
                                <FilterMultiDropdown
                                    label="Tipo"
                                    icon="fa fa-tag"
                                    :options="typeOptions"
                                    v-model="selectedTypes"
                                />
                            </div>

                            <div class="d-flex-row flex-justify-end flex-align-center m-t-md" style="gap: 16px;">
                                <span class="info">
                                    <strong>{{ filteredTests.length }}</strong> {{ filteredTests.length === 1 ? 'avaliação' : 'avaliações' }}
                                </span>
                                <button v-if="hasActiveFilters" class="btn btn-default" @click="clearFilters">
                                    Limpar filtros
                                </button>
                            </div>
                        </div>
                    </div>

                    <div
                        v-for="test in sortedTests"
                        :key="test.item_generated"
                        class="col-xs-12 col-md-6 col-lg-3 p-md m-b-md"
                    >
                        <BaseTestCard :test="test" :module-name="test.module">
                            <template #footer>
                                <div class="col-xs-12 p-0 m-b-md text-left" v-if="test.end_date && !test.configs.hide_end_date">
                                    <span>Encerramento: {{ formatDbDatetime(test.end_date) }}</span>
                                </div>
                                <div class="col-xs-12 p-0 m-b-md d-flex-row flex-justify-between flex-align-center" v-if="!test.configs.param_hide_online_test_date">
                                    <span class="info">
                                        <i class="fa fa-calendar"></i> {{ formatDbDate(test.schedule.date_schedule) }} às {{ test.schedule.time_schedule }}
                                    </span>
                                    <span class="info">
                                        <i class="fa fa-clock-o"></i> {{ test.online_test_duration }} minutos
                                    </span>
                                </div>
                                <div class="col-xs-12">
                                    <div v-if="test.application_mode !== 'p'">
                                        <router-link
                                            v-if="!test.configs.use_examus_monitoring"
                                            role="button"
                                            class="col-xs-12 btn btn-default"
                                            :to="`/online-tests/${test.item_generated}/instructions`"
                                        >
                                            Acessar avaliação
                                        </router-link>
                                        <a
                                            v-else
                                            role="button"
                                            class="col-xs-12 btn btn-default"
                                            :href="getBaseSgpUrl() + 'examusonlinetest/' + test.item_generated + '?use_new_layout=true'"
                                        >
                                            Acessar avaliação
                                        </a>
                                    </div>
                                    <button v-else class="col-xs-12 btn btn-default" disabled>
                                        Prova impressa
                                    </button>
                                </div>
                            </template>
                        </BaseTestCard>
                    </div>

                    <div v-if="filteredTests.length === 0" class="col-xs-12 d-flex-row flex-align-center flex-justify-center p-xl">
                        <img src="../../assets/img/search-logo.png" alt="Nenhum resultado" class="m-r-xl" />
                        <div class="d-flex-column">
                            <div class="no-results-title">Nenhum resultado encontrado</div>
                            <div>Não há registros que atendam aos filtros aplicados</div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="col-xs-12 d-flex-row flex-align-center flex-justify-center p-xl">
                <img src="../../assets/img/no-results-logo.png" alt="Nenhum resultado" class="m-r-xl" />
                <div class="d-flex-column">
                    <div class="no-results-title">Nenhum resultado encontrado</div>
                    <div>Não há provas disponíveis para serem realizadas.</div>
                </div>
            </div>
        </div>

        <div v-else role="presentation" class="col-xs-12 d-flex-column align-center flex-justify-center text-center h-100vh overflow-hidden">
            <i class="fa fa-5x fa-circle-o-notch fa-spin"></i>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { getOnlineTests } from '@/scripts/apiService/tests';
import { extractModule, formatDbDate, formatDbDatetime } from '@/scripts/utils';
import { getBaseSgpUrl } from '@/scripts/apiService/ApiService';
import FilterMultiDropdown from '../base/FilterMultiDropdown.vue';
import BaseTestCard from '../base/BaseTestCard.vue';


const route = useRoute();

const result = ref(null);
const loading = ref(false);
const searchQuery = ref('');
const activeSearchQuery = ref('');
const selectedModules = ref([]);
const selectedTypes = ref([]);

const moduleOptions = computed(() => {
    if (!result.value) return [];
    const modules = [...new Set(result.value.map(t => t.module_name || extractModule(t.academic)).filter(Boolean))].sort();
    return modules.map(m => ({ value: m, label: m }));
});

const typeOptions = computed(() => {
    if (!result.value) return [];
    const types = [...new Set(result.value.map(t => t.schedule.type_assessment.name))].sort();
    return types.map(t => ({ value: t, label: t }));
});

watch(searchQuery, (val) => {
    if (val.length === 0 || val.length >= 3) {
        activeSearchQuery.value = val;
    }
});

const hasActiveFilters = computed(() =>
    activeSearchQuery.value || selectedModules.value.length || selectedTypes.value.length
);

const testsWithData = computed(() => {
    if (!result.value) return [];
    return result.value.map(test => ({
        ...test,
        module: test.module_name || extractModule(test.academic),
    }));
});

const filteredTests = computed(() => {
    let tests = testsWithData.value;

    if (activeSearchQuery.value) {
        const normalize = str => str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const query = normalize(activeSearchQuery.value);
        tests = tests.filter(t =>
            normalize(t.academic).includes(query) ||
            normalize(t.schedule.type_assessment.name).includes(query)
        );
    }

    if (selectedModules.value.length) {
        tests = tests.filter(t => selectedModules.value.includes(t.module));
    }

    if (selectedTypes.value.length) {
        tests = tests.filter(t => selectedTypes.value.includes(t.schedule.type_assessment.name));
    }

    return tests;
});

const sortedTests = computed(() => {
    return [...filteredTests.value].sort((a, b) => {
        const moduleCompare = (a.module || '').localeCompare(b.module || '');
        if (moduleCompare !== 0) return moduleCompare;

        const dateCompare = (a.schedule?.date_schedule || '').localeCompare(b.schedule?.date_schedule || '');
        if (dateCompare !== 0) return dateCompare;

        const timeCompare = (a.schedule?.time_schedule || '').localeCompare(b.schedule?.time_schedule || '');
        if (timeCompare !== 0) return timeCompare;

        return (a.schedule?.type_assessment?.name || '').localeCompare(b.schedule?.type_assessment?.name || '');
    });
});

function clearFilters() {
    searchQuery.value = '';
    activeSearchQuery.value = '';
    selectedModules.value = [];
    selectedTypes.value = [];
}

onMounted(() => {
    loading.value = true;
    getOnlineTests(route.params.key)
        .then((response) => {
            result.value = response.data;
        })
        .catch(() => {})
        .finally(() => {
            loading.value = false;
        });
    document.getElementById('title')?.focus();
});
</script>
